import * as THREE from 'three';

export type XZPoint = { x: number; z: number };

export interface LandmarkOptions {
  label?: string;
  color?: number;
  accent?: number;
  gradientMap?: THREE.DataTexture;
  cullDistance?: number;
}

function landmarkMat(color: number, gradientMap?: THREE.DataTexture, emissiveIntensity = 0.12): THREE.Material {
  if (gradientMap) {
    const mat = new THREE.MeshToonMaterial({ color, gradientMap });
    mat.emissive.setHex(new THREE.Color(color).multiplyScalar(0.28).getHex());
    mat.emissiveIntensity = emissiveIntensity;
    return mat;
  }
  return new THREE.MeshStandardMaterial({
    color,
    roughness: 0.58,
    metalness: 0.08,
    emissive: new THREE.Color(color).multiplyScalar(0.18).getHex(),
    emissiveIntensity,
  });
}

function makeLabelTexture(label: string, color: number, width = 256, height = 96): THREE.CanvasTexture {
  const cv = document.createElement('canvas');
  cv.width = width;
  cv.height = height;
  const ctx = cv.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D context unavailable');
  const hex = `#${color.toString(16).padStart(6, '0')}`;
  const fill = ctx.createLinearGradient(0, 0, width, height);
  fill.addColorStop(0, hex);
  fill.addColorStop(1, '#171525');
  ctx.fillStyle = fill;
  ctx.fillRect(0, 0, width, height);
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = 5;
  ctx.strokeRect(5, 5, width - 10, height - 10);
  ctx.font = 'bold 32px system-ui,sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.lineWidth = 6;
  ctx.strokeStyle = '#111111';
  ctx.strokeText(label, width / 2, height / 2);
  ctx.fillStyle = '#ffffff';
  ctx.fillText(label, width / 2, height / 2);
  const tex = new THREE.CanvasTexture(cv);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

export function markDecorative(object: THREE.Object3D, cullDistance = 130): THREE.Object3D {
  object.userData.decorative = true;
  object.userData.cullDistance = cullDistance;
  object.traverse((child) => {
    child.userData.decorative = true;
    child.userData.cullDistance = cullDistance;
    child.raycast = () => undefined;
  });
  return object;
}

export function markAmbientPulse(
  object: THREE.Object3D,
  opts: { material?: THREE.Material; light?: THREE.Light; base?: number; amount?: number; speed?: number; phase?: number } = {},
): THREE.Object3D {
  object.userData.ambientPulse = opts;
  return object;
}

export function markAmbientSpin(object: THREE.Object3D, speedY = 0.22): THREE.Object3D {
  object.userData.ambientSpin = { speedY };
  return object;
}

export function buildPathStrip(start: XZPoint, end: XZPoint, width: number, color: number): THREE.Mesh {
  const dx = end.x - start.x;
  const dz = end.z - start.z;
  const length = Math.hypot(dx, dz);
  const mat = new THREE.MeshStandardMaterial({ color, roughness: 0.9, metalness: 0 });
  const mesh = new THREE.Mesh(new THREE.BoxGeometry(length, 0.045, width), mat);
  mesh.position.set((start.x + end.x) / 2, 0.035, (start.z + end.z) / 2);
  mesh.rotation.y = -Math.atan2(dz, dx);
  mesh.receiveShadow = true;
  markDecorative(mesh, 170);
  return mesh;
}

export function buildNeonAccentStrip(opts: LandmarkOptions & { length?: number } = {}): THREE.Group {
  const color = opts.color ?? 0xff44cc;
  const accent = opts.accent ?? 0xffffff;
  const g = new THREE.Group();
  const railMat = landmarkMat(0x202534, opts.gradientMap, 0.08);
  const glowMat = new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.9 });
  const length = opts.length ?? 2.8;

  const base = new THREE.Mesh(new THREE.BoxGeometry(length, 0.08, 0.16), railMat);
  base.position.y = 0.08;
  base.castShadow = true;
  g.add(base);

  for (const z of [-0.13, 0.13]) {
    const stripMat = glowMat.clone();
    const strip = new THREE.Mesh(new THREE.BoxGeometry(length * 0.92, 0.045, 0.045), stripMat);
    strip.position.set(0, 0.18, z);
    markAmbientPulse(strip, { material: stripMat, base: 0.86, amount: 0.12, speed: 1.4, phase: z * 9 });
    g.add(strip);
  }

  const cap = new THREE.Mesh(new THREE.BoxGeometry(0.18, 0.12, 0.34), landmarkMat(accent, opts.gradientMap, 0.22));
  cap.position.set(length * 0.5, 0.12, 0);
  g.add(cap, cap.clone());
  (g.children[g.children.length - 1] as THREE.Object3D).position.x = -length * 0.5;

  markDecorative(g, opts.cullDistance ?? 120);
  return g;
}

export function buildFlagBanner(opts: LandmarkOptions & { width?: number; height?: number } = {}): THREE.Group {
  const color = opts.color ?? 0xffcc44;
  const accent = opts.accent ?? 0x2b2233;
  const g = new THREE.Group();
  const postMat = landmarkMat(0x5c3a24, opts.gradientMap, 0.1);
  const clothMat = landmarkMat(color, opts.gradientMap, 0.28);
  const trimMat = landmarkMat(accent, opts.gradientMap, 0.16);
  const width = opts.width ?? 1.15;
  const height = opts.height ?? 0.78;

  const post = new THREE.Mesh(new THREE.CylinderGeometry(0.045, 0.06, 2.6, 7), postMat);
  post.position.y = 1.3;
  post.castShadow = true;
  g.add(post);

  const cross = new THREE.Mesh(new THREE.BoxGeometry(width + 0.3, 0.06, 0.06), postMat);
  cross.position.set(width * 0.48, 2.35, 0);
  g.add(cross);

  const banner = new THREE.Mesh(new THREE.PlaneGeometry(width, height), clothMat);
  banner.position.set(width * 0.5, 1.92, 0.02);
  banner.rotation.z = -0.08;
  banner.castShadow = true;
  g.add(banner);

  const stripe = new THREE.Mesh(new THREE.BoxGeometry(width * 0.9, 0.055, 0.035), trimMat);
  stripe.position.set(width * 0.5, 1.58, 0.04);
  g.add(stripe);

  markDecorative(g, opts.cullDistance ?? 120);
  return g;
}

export function buildRouteSightlineScreen(opts: LandmarkOptions & { width?: number; height?: number } = {}): THREE.Group {
  const color = opts.color ?? 0x8aa0a8;
  const accent = opts.accent ?? 0x2b2233;
  const width = opts.width ?? 2.1;
  const height = opts.height ?? 2.2;
  const g = new THREE.Group();
  const postMat = landmarkMat(accent, opts.gradientMap, 0.1);
  const panelMat = landmarkMat(color, opts.gradientMap, 0.18);

  for (const x of [-width * 0.5, width * 0.5]) {
    const post = new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.08, height + 0.35, 7), postMat);
    post.position.set(x, (height + 0.35) * 0.5, 0);
    post.castShadow = true;
    g.add(post);
  }

  const panel = new THREE.Mesh(new THREE.BoxGeometry(width, height * 0.68, 0.16), panelMat);
  panel.position.y = height * 0.56;
  panel.castShadow = true;
  panel.receiveShadow = true;
  g.add(panel);

  const cap = new THREE.Mesh(new THREE.BoxGeometry(width + 0.28, 0.12, 0.22), postMat);
  cap.position.y = height + 0.1;
  cap.castShadow = true;
  g.add(cap);

  markDecorative(g, opts.cullDistance ?? 125);
  return g;
}

export function buildSignalDish(opts: LandmarkOptions = {}): THREE.Group {
  const color = opts.color ?? 0x67d8ff;
  const accent = opts.accent ?? 0xffffff;
  const g = new THREE.Group();
  const metal = landmarkMat(0x596777, opts.gradientMap, 0.1);
  const glow = landmarkMat(color, opts.gradientMap, 0.42);

  const tripod = new THREE.Mesh(new THREE.CylinderGeometry(0.055, 0.075, 1.45, 7), metal);
  tripod.position.y = 0.72;
  tripod.castShadow = true;
  g.add(tripod);

  const dish = new THREE.Mesh(new THREE.ConeGeometry(0.62, 0.34, 18, 1, true), metal);
  dish.position.set(0, 1.56, 0);
  dish.rotation.x = Math.PI * 0.5;
  dish.castShadow = true;
  g.add(dish);

  const emitter = new THREE.Mesh(new THREE.SphereGeometry(0.11, 10, 8), glow);
  emitter.position.set(0, 1.56, 0.38);
  markAmbientPulse(emitter, { material: glow, base: 0.42, amount: 0.12, speed: 1.8 });
  g.add(emitter);

  const pulse = new THREE.Mesh(
    new THREE.TorusGeometry(0.74, 0.025, 7, 24),
    new THREE.MeshBasicMaterial({ color: accent, transparent: true, opacity: 0.56 }),
  );
  pulse.position.set(0, 1.56, 0.5);
  markAmbientPulse(pulse, { material: pulse.material, base: 0.48, amount: 0.16, speed: 1.8, phase: 0.5 });
  markAmbientSpin(pulse, 0.18);
  g.add(pulse);

  markDecorative(g, opts.cullDistance ?? 135);
  return g;
}

export function buildBiomeAccentCluster(opts: LandmarkOptions & { count?: number } = {}): THREE.Group {
  const color = opts.color ?? 0x6cd7ff;
  const accent = opts.accent ?? 0xffffff;
  const g = new THREE.Group();
  const colors = [color, accent, new THREE.Color(color).lerp(new THREE.Color(accent), 0.45).getHex()];
  for (let i = 0; i < (opts.count ?? 5); i++) {
    const a = (i / (opts.count ?? 5)) * Math.PI * 2;
    const r = 0.35 + (i % 3) * 0.28;
    const pebble = new THREE.Mesh(
      new THREE.DodecahedronGeometry(0.16 + (i % 2) * 0.05, 0),
      landmarkMat(colors[i % colors.length]!, opts.gradientMap, 0.22),
    );
    pebble.position.set(Math.cos(a) * r, 0.14, Math.sin(a) * r);
    pebble.rotation.set(i * 0.4, a, i * 0.2);
    pebble.castShadow = true;
    pebble.receiveShadow = true;
    g.add(pebble);
  }
  markDecorative(g, opts.cullDistance ?? 95);
  return g;
}

export function buildLandmarkSign(label: string, opts: LandmarkOptions = {}): THREE.Group {
  const color = opts.color ?? 0xffcc44;
  const accent = opts.accent ?? 0x2b2233;
  const g = new THREE.Group();
  const postMat = landmarkMat(0x5c3a24, opts.gradientMap);
  const frameMat = landmarkMat(accent, opts.gradientMap);
  for (const x of [-1.25, 1.25]) {
    const post = new THREE.Mesh(new THREE.CylinderGeometry(0.09, 0.12, 4.2, 8), postMat);
    post.position.set(x, 2.1, 0);
    post.castShadow = true;
    g.add(post);
  }
  const board = new THREE.Mesh(
    new THREE.PlaneGeometry(3.4, 1.15),
    new THREE.MeshStandardMaterial({
      map: makeLabelTexture(label, color),
      emissive: 0xffffff,
      emissiveMap: makeLabelTexture(label, color),
      emissiveIntensity: 0.32,
      side: THREE.DoubleSide,
    }),
  );
  board.position.y = 3.25;
  g.add(board);
  const cap = new THREE.Mesh(new THREE.BoxGeometry(3.8, 0.16, 0.16), frameMat);
  cap.position.y = 3.95;
  g.add(cap);
  markDecorative(g, opts.cullDistance ?? 140);
  return g;
}

export function buildWaterTower(opts: LandmarkOptions = {}): THREE.Group {
  const g = new THREE.Group();
  const color = opts.color ?? 0x5db7ff;
  const metal = landmarkMat(0x5c6b78, opts.gradientMap, 0.08);
  const tankMat = landmarkMat(color, opts.gradientMap, 0.2);
  for (const [x, z] of [[-1, -1], [1, -1], [-1, 1], [1, 1]] as const) {
    const leg = new THREE.Mesh(new THREE.CylinderGeometry(0.07, 0.1, 6.4, 7), metal);
    leg.position.set(x, 3.2, z);
    leg.rotation.z = -x * 0.08;
    leg.rotation.x = z * 0.08;
    leg.castShadow = true;
    g.add(leg);
  }
  const tank = new THREE.Mesh(new THREE.CylinderGeometry(1.35, 1.55, 2.1, 16), tankMat);
  tank.position.y = 6.8;
  tank.castShadow = true;
  g.add(tank);
  const roof = new THREE.Mesh(new THREE.ConeGeometry(1.55, 0.85, 16), landmarkMat(opts.accent ?? 0xfff2b0, opts.gradientMap, 0.16));
  roof.position.y = 8.28;
  roof.castShadow = true;
  g.add(roof);
  markDecorative(g, opts.cullDistance ?? 180);
  return g;
}

export function buildCrane(opts: LandmarkOptions = {}): THREE.Group {
  const g = new THREE.Group();
  const yellow = landmarkMat(opts.color ?? 0xffc23d, opts.gradientMap, 0.18);
  const dark = landmarkMat(opts.accent ?? 0x473018, opts.gradientMap, 0.08);
  const mast = new THREE.Mesh(new THREE.BoxGeometry(0.35, 8.2, 0.35), yellow);
  mast.position.y = 4.1;
  mast.castShadow = true;
  g.add(mast);
  const arm = new THREE.Mesh(new THREE.BoxGeometry(7.8, 0.28, 0.28), yellow);
  arm.position.set(2.9, 7.75, 0);
  arm.castShadow = true;
  g.add(arm);
  const counter = new THREE.Mesh(new THREE.BoxGeometry(1.45, 0.55, 0.55), dark);
  counter.position.set(-1.15, 7.72, 0);
  g.add(counter);
  const cable = new THREE.Mesh(new THREE.CylinderGeometry(0.025, 0.025, 2.2, 6), dark);
  cable.position.set(5.8, 6.55, 0);
  g.add(cable);
  const hook = new THREE.Mesh(new THREE.TorusGeometry(0.22, 0.045, 8, 16), dark);
  hook.position.set(5.8, 5.4, 0);
  g.add(hook);
  markDecorative(g, opts.cullDistance ?? 180);
  return g;
}

export function buildNeonBillboard(label: string, opts: LandmarkOptions = {}): THREE.Group {
  const g = buildLandmarkSign(label, { ...opts, cullDistance: opts.cullDistance ?? 190 });
  const color = opts.color ?? 0xff44cc;
  const glow = new THREE.PointLight(color, 1.5, 13);
  glow.position.set(0, 3.2, 0.6);
  markAmbientPulse(glow, { light: glow, base: 1.35, amount: 0.28, speed: 1.25 });
  g.add(glow);
  const halo = new THREE.Mesh(
    new THREE.TorusGeometry(2.05, 0.055, 8, 28),
    new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.78 }),
  );
  halo.position.y = 3.25;
  markAmbientPulse(halo, { material: halo.material, base: 0.7, amount: 0.12, speed: 1.25 });
  markAmbientSpin(halo, 0.08);
  g.add(halo);
  markDecorative(g, opts.cullDistance ?? 190);
  return g;
}

export function buildRadioMast(opts: LandmarkOptions = {}): THREE.Group {
  const g = new THREE.Group();
  const metal = landmarkMat(opts.color ?? 0xdfe8f2, opts.gradientMap, 0.1);
  const red = landmarkMat(opts.accent ?? 0xff4242, opts.gradientMap, 0.35);
  const mast = new THREE.Mesh(new THREE.CylinderGeometry(0.08, 0.12, 11.5, 8), metal);
  mast.position.y = 5.75;
  mast.castShadow = true;
  g.add(mast);
  for (const y of [2.4, 4.6, 6.8, 9.0]) {
    const cross = new THREE.Mesh(new THREE.BoxGeometry(2.4, 0.08, 0.08), metal);
    cross.position.y = y;
    cross.rotation.y = y * 0.7;
    g.add(cross);
  }
  const beacon = new THREE.Mesh(new THREE.SphereGeometry(0.18, 10, 8), red);
  beacon.position.y = 11.75;
  markAmbientPulse(beacon, { material: red, base: 0.28, amount: 0.24, speed: 1.1 });
  g.add(beacon);
  const beaconLight = new THREE.PointLight(opts.accent ?? 0xff4242, 1.2, 10);
  beaconLight.position.y = 11.75;
  markAmbientPulse(beaconLight, { light: beaconLight, base: 0.95, amount: 0.45, speed: 1.1 });
  g.add(beaconLight);
  markDecorative(g, opts.cullDistance ?? 200);
  return g;
}

export function buildDockLight(opts: LandmarkOptions = {}): THREE.Group {
  const g = new THREE.Group();
  const postMat = landmarkMat(opts.color ?? 0x2d4052, opts.gradientMap, 0.12);
  const glowColor = opts.accent ?? 0xfff3a3;
  const post = new THREE.Mesh(new THREE.CylinderGeometry(0.09, 0.12, 5.2, 8), postMat);
  post.position.y = 2.6;
  post.castShadow = true;
  g.add(post);
  const arm = new THREE.Mesh(new THREE.BoxGeometry(1.55, 0.11, 0.11), postMat);
  arm.position.set(0.7, 5.05, 0);
  g.add(arm);
  const lamp = new THREE.Mesh(new THREE.SphereGeometry(0.28, 12, 8), new THREE.MeshBasicMaterial({ color: glowColor }));
  lamp.position.set(1.45, 4.85, 0);
  const lampLight = new THREE.PointLight(glowColor, 1.7, 12);
  lampLight.position.copy(lamp.position);
  markAmbientPulse(lampLight, { light: lampLight, base: 1.55, amount: 0.18, speed: 0.9 });
  g.add(lamp, lampLight);
  markDecorative(g, opts.cullDistance ?? 160);
  return g;
}

export type FpsPoiZone = {
  name: string;
  x: number;
  z: number;
  radius: number;
};

/** Named POI zones for landing toasts, minimap labels, and visit tracking. */
export const FPS_POI_ZONES: readonly FpsPoiZone[] = [
  { name: 'TILTED TOWERS', x: 2, z: -38, radius: 16 },
  { name: 'RETAIL ROW', x: -35, z: 22, radius: 14 },
  { name: 'PLEASANT PARK', x: 40, z: 15, radius: 15 },
  { name: 'LUCKY LANDING', x: -22, z: -28, radius: 13 },
  { name: 'CRACKED COVE', x: 28, z: -22, radius: 14 },
  { name: 'DUSTY DEPOT', x: 32, z: -32, radius: 12 },
  { name: 'SNOBBY SHORES', x: -30, z: 30, radius: 18 },
  { name: 'SHIFTY SHAFTS', x: -18, z: -12, radius: 13 },
  { name: 'PIVOT PLAZA', x: -55, z: -55, radius: 20 },
] as const;

export const FPS_POI_ZONE_COLORS: Record<
  (typeof FPS_POI_ZONES)[number]['name'],
  { bg: string; border: string; glow: string }
> = {
  'TILTED TOWERS': { bg: 'rgba(200,55,70,0.26)', border: 'rgba(255,95,115,0.82)', glow: 'rgba(255,70,95,0.55)' },
  'RETAIL ROW': { bg: 'rgba(210,145,35,0.24)', border: 'rgba(255,205,70,0.8)', glow: 'rgba(255,190,55,0.52)' },
  'PLEASANT PARK': { bg: 'rgba(55,150,95,0.24)', border: 'rgba(90,230,150,0.78)', glow: 'rgba(70,210,120,0.5)' },
  'LUCKY LANDING': { bg: 'rgba(180,90,210,0.24)', border: 'rgba(220,140,255,0.78)', glow: 'rgba(200,110,255,0.5)' },
  'CRACKED COVE': { bg: 'rgba(45,130,195,0.24)', border: 'rgba(80,190,255,0.78)', glow: 'rgba(60,175,255,0.5)' },
  'DUSTY DEPOT': { bg: 'rgba(170,120,55,0.24)', border: 'rgba(230,180,90,0.78)', glow: 'rgba(220,165,70,0.5)' },
  'SNOBBY SHORES': { bg: 'rgba(55,120,195,0.24)', border: 'rgba(100,180,255,0.78)', glow: 'rgba(80,165,255,0.5)' },
  'SHIFTY SHAFTS': { bg: 'rgba(130,95,55,0.26)', border: 'rgba(200,155,90,0.78)', glow: 'rgba(185,135,70,0.5)' },
  'PIVOT PLAZA': { bg: 'rgba(30,50,120,0.26)', border: 'rgba(80,120,255,0.82)', glow: 'rgba(60,100,255,0.55)' },
};

/** Returns the POI zone containing world XZ, if any. */
export function findPoiZoneAt(px: number, pz: number): FpsPoiZone | null {
  for (const zone of FPS_POI_ZONES) {
    const dx = px - zone.x;
    const dz = pz - zone.z;
    if (dx * dx + dz * dz <= zone.radius * zone.radius) return zone;
  }
  return null;
}

const poiTintScratch = new THREE.Color();

function parseRgbaGlow(glow: string): THREE.Color {
  const m = glow.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (!m) return poiTintScratch.setRGB(0.45, 0.72, 0.95);
  return poiTintScratch.setRGB(+m[1]! / 255, +m[2]! / 255, +m[3]! / 255);
}

/** Grade-pass tint color derived from a POI zone's glow palette. */
export function getPoiAmbientGradeTint(name: string): THREE.Color {
  return parseRgbaGlow(getPoiZoneColors(name).glow).clone();
}

/** 0–1 blend strength for POI ambient tint (strongest at zone center). */
export function getPoiTintBlendAt(px: number, pz: number, zone: FpsPoiZone): number {
  const dx = px - zone.x;
  const dz = pz - zone.z;
  const dist = Math.hypot(dx, dz);
  if (dist >= zone.radius) return 0;
  const inner = zone.radius * 0.32;
  return THREE.MathUtils.smoothstep(zone.radius - dist, 0, Math.max(0.001, zone.radius - inner));
}

export function getPoiZoneColors(name: string): { bg: string; border: string; glow: string } {
  return FPS_POI_ZONE_COLORS[name as keyof typeof FPS_POI_ZONE_COLORS] ?? {
    bg: 'rgba(46,120,180,0.24)',
    border: 'rgba(80,190,255,0.78)',
    glow: 'rgba(60,175,255,0.5)',
  };
}

export function buildDepotSilo(opts: LandmarkOptions = {}): THREE.Group {
  const g = new THREE.Group();
  const siloMat = landmarkMat(opts.color ?? 0xd8c1a0, opts.gradientMap, 0.14);
  const capMat = landmarkMat(opts.accent ?? 0x9a6532, opts.gradientMap, 0.16);
  const body = new THREE.Mesh(new THREE.CylinderGeometry(1.25, 1.45, 6.8, 18), siloMat);
  body.position.y = 3.4;
  body.castShadow = true;
  body.receiveShadow = true;
  g.add(body);
  const cap = new THREE.Mesh(new THREE.ConeGeometry(1.45, 1.25, 18), capMat);
  cap.position.y = 7.45;
  cap.castShadow = true;
  g.add(cap);
  const pipe = new THREE.Mesh(new THREE.CylinderGeometry(0.12, 0.12, 5.2, 8), capMat);
  pipe.position.set(1.45, 3.0, 0);
  pipe.rotation.z = -0.22;
  g.add(pipe);
  markDecorative(g, opts.cullDistance ?? 175);
  return g;
}
